import {
  CheckIcon,
  DesktopIcon,
  GKButton,
  Grid2,
  IconButton,
  LeftIcon,
  MobileIcon,
  RightIcon,
  SendIcon,
  Show,
  TabletIcon,
  Typography,
} from "components";
import { map, split } from "lodash";
import InvestorSettingsConfirmation from "Pages/Settings/InvestorSettingsConfirmation";
import { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useGetMyWebsiteDetailsQuery,
  useUpdateFaqEntriesMutation,
  useUpdateMyWebsiteDetailsMutation,
  useUpdateTestimonialsMutation,
  useUploadComplaintsMutation,
} from "store/features/MyWebsite";
import {
  setOpenExtraSection,
  setPreviewMode,
  setSelectedSection,
} from "store/slices/MyWebsiteSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { handleFreshReturnFormData } from "utils";
import { handleCreateFormData } from "utils/formHelpers";
import useDocumentTitle from "utils/useDocumentTitle";
import {
  ExtraSectionItem,
  FormFooter,
  SectionHeader,
  WebsiteSectionCard,
} from "./ExtraComponent";
import Preview from "./Preview";
import { PreviewMode, WebsiteDetails } from "./types";
import {
  processFileField,
  websiteExtraSection,
  websiteSectionButtons,
} from "./utils";
import WebsiteForm from "./WebsiteForm";

const getFileInfo = (url?: string) => {
  if (!url) return [];
  const fileName = split(split(url, "?")[0], "/").pop() || "";
  return [{ name: fileName, file: url, size: 0 }];
};

const MyWebsite = () => {
  useDocumentTitle("My Website");

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const userData = useAppSelector((state) => state?.user?.userData);
  const { selectedSection, openExtraSection, previewMode } = useAppSelector(
    (state) => state?.myWebsite,
  );

  const [websiteConfiguration, setWebsiteConfiguration] = useState(false);

  const { data, isFetching } = useGetMyWebsiteDetailsQuery(userData?.companyId);

  const [updateMyWebsiteDetails, { isLoading }] =
    useUpdateMyWebsiteDetailsMutation();

  const [uploadComplaints, { isLoading: isLoadingComplaints }] =
    useUploadComplaintsMutation();

  const [updateFaqEntries, { isLoading: isLoadingFaqEntries }] =
    useUpdateFaqEntriesMutation();

  const [updateTestimonials, { isLoading: isLoadingTestimonials }] =
    useUpdateTestimonialsMutation();

  const form = useForm<WebsiteDetails>({
    values: {
      ...data?.data,
      compliance: {
        ...data?.data?.compliance,
        investmentCharter: getFileInfo(
          data?.data?.compliance?.investmentCharter,
        ),
        complianceAudit:
          data?.data?.compliance?.complianceAudit?.length !== 0
            ? data?.data?.compliance?.complianceAudit?.map((d: any) => {
                return {
                  ...d,
                  year: d?.year,
                  report1: getFileInfo(d?.report1),
                  report2: getFileInfo(d?.report2),
                };
              })
            : [
                {
                  report1: [],
                  report2: [],
                  year: "",
                },
              ],
      },
      websiteSection: {
        ...data?.data?.websiteSection,
        myBranding: {
          ...data?.data?.websiteSection?.myBranding,
          colorPalette: data?.data?.websiteSection?.myBranding?.colorPalette
            ? JSON.parse(data?.data?.websiteSection?.myBranding?.colorPalette)
            : {
                primary: "#006580",
                secondary: "#63A387",
              },
        },
        faqs: data?.data?.websiteSection?.faqs?.map((dataItem) => {
          return {
            ...dataItem,
            rowId: dataItem?.id,
          };
        }),
        testimonials: data?.data?.websiteSection?.testimonials?.map(
          (dataItem) => {
            return {
              ...dataItem,
              rowId: dataItem?.id,
            };
          },
        ),
      },
    },
  });

  const handleNavigate = useCallback(() => navigate(-1), [navigate]);
  const handleOpenExtraSection = useCallback(
    (type: string) => dispatch(setOpenExtraSection(type)),
    [],
  );
  const handleSectionClick = useCallback(
    (sectionValue: string) => {
      dispatch(setOpenExtraSection(null));
      dispatch(setSelectedSection(sectionValue));
    },
    [dispatch],
  );
  const handleBackToSections = useCallback(() => {
    dispatch(setOpenExtraSection(null));
    dispatch(setSelectedSection(null));
  }, [dispatch]);

  const modeChangeHandler = useCallback(
    (type: PreviewMode) => {
      dispatch(setPreviewMode(type));
    },
    [dispatch],
  );

  const handleSectionUpdate = async (
    path: string,
    obj: Record<string, any>,
    options: { isEditForm?: boolean } = {},
  ) => {
    const formData = handleFreshReturnFormData({
      isEditForm: options.isEditForm ?? false,
      obj,
    });

    await updateMyWebsiteDetails({
      id: userData?.companyId,
      body: formData,
      path,
    }).unwrap();
  };

  const onSubmit = async (values: WebsiteDetails) => {
    const newObject = { ...values };

    try {
      /** -------------------- WEBSITE SECTIONS -------------------- */
      const section = selectedSection;
      const extra = openExtraSection;

      switch (section) {
        case "MY_BRANDING": {
          const data = { ...newObject.websiteSection?.myBranding };
          processFileField(data, "companyLogo");
          await handleSectionUpdate("my_branding", data);
          break;
        }

        case "HERO_SECTION": {
          const data = { ...newObject.websiteSection?.heroSection };
          processFileField(data, "advisorPhoto");
          await handleSectionUpdate("hero_section", data);
          break;
        }

        case "PERFORMANCE_METRICS": {
          const data = {
            ...newObject.websiteSection?.performanceMetrices,
            pmShowOnWebsite: newObject.websiteSection?.performanceMetrices
              ?.pmShowOnWebsite
              ? "true"
              : "false",
          };
          await handleSectionUpdate("performance_metrices", data);
          break;
        }

        case "QUOTE_MANTRA": {
          const data = { ...newObject.websiteSection?.quotes };
          processFileField(data, "quoteOwnerUrl");
          await handleSectionUpdate("quotes", data);
          break;
        }

        case "ABOUT_ME": {
          const data = { ...newObject.websiteSection?.aboutMe };
          processFileField(data, "youtubeThumbnail");
          await handleSectionUpdate("about_me", data);
          break;
        }

        case "FOOTER": {
          const data = { ...newObject.websiteSection?.footer };
          await handleSectionUpdate("footer", data);
          break;
        }

        case "FAQS": {
          await updateFaqEntries({
            body: values.websiteSection?.faqs,
          }).unwrap();
          break;
        }

        case "TESTIMONIALS": {
          await updateTestimonials({
            body: values.websiteSection?.testimonials,
          }).unwrap();
          break;
        }

        default: {
          break;
        }
      }

      /** -------------------- EXTRA SECTIONS -------------------- */
      switch (extra) {
        case "WEBSITE_CONFIGURATION": {
          await handleSectionUpdate(
            "publish_settings/config",
            newObject.websiteConfiguration,
          );
          break;
        }

        case "INVESTMENT_CHARTER": {
          const formData = handleCreateFormData({
            dirtyFields: form.formState.dirtyFields.compliance,
            values: newObject.compliance,
          });
          await updateMyWebsiteDetails({
            id: userData?.companyId,
            body: formData,
            path: "investment_charter",
          }).unwrap();
          break;
        }

        case "TERMS_CONDITIONS": {
          await handleSectionUpdate(
            "terms_con",
            {
              termsConditions: newObject.compliance?.termsConditions,
            },
            { isEditForm: true },
          );
          break;
        }

        case "POLICIES": {
          await handleSectionUpdate(
            "policies",
            {
              ...newObject.compliance?.policies,
            },
            { isEditForm: true },
          );
          break;
        }

        case "DISCLOSURE": {
          await handleSectionUpdate(
            "disclosure",
            {
              disclosure: newObject.compliance?.disclosure,
            },
            { isEditForm: true },
          );
          break;
        }

        case "COMPLAINTS_RESOLUTION": {
          const formData = new FormData();
          const cr = values?.compliance?.complaintResolution;

          if (cr?.monthly?.length === 1)
            formData.append("monthly", cr.monthly[0]);
          if (cr?.monthWise?.length === 1)
            formData.append("monthWise", cr.monthWise[0]);
          if (cr?.annual?.length === 1) formData.append("annual", cr.annual[0]);

          await uploadComplaints(formData).unwrap();
          break;
        }

        case "GRIEVANCE_COMPLIANCE_OFFICER_DETAILS": {
          await handleSectionUpdate(
            "grievance_comp",
            values?.compliance?.grievanceComplainceOfficerDetails,
            { isEditForm: true },
          );
          break;
        }

        case "COMPLIANCE_AUDIT_REPORT": {
          const dirty = form.formState?.dirtyFields.compliance?.complianceAudit;
          const formData = handleCreateFormData({
            values: values.compliance,
            dirtyFields: {
              complianceAudit: Array.from({ length: dirty?.length ?? 0 }).map(
                () => ({ year: true, report1: true, report2: true }),
              ),
            },
          });

          await updateMyWebsiteDetails({
            id: userData?.companyId,
            body: formData,
            path: "compliance_audit",
          }).unwrap();
          break;
        }

        default: {
          break;
        }
      }
    } catch (err) {
      console.error("Update section failed:", err);
    }
  };

  return (
    <>
      <FormProvider {...form}>
        <form id="my-website-form" onSubmit={form.handleSubmit(onSubmit)}>
          <Grid2 container height={"100vh"} alignContent={"flex-start"}>
            <Grid2
              size={12}
              p={"8px 24px"}
              borderBottom={"1px solid var(--neutral-100)"}
              bgcolor={"var(--white)"}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Typography
                fontSize={18}
                fontWeight={700}
                color="var(--black)"
                sx={{ cursor: "pointer" }}
                display={"flex"}
                alignItems={"center"}
                gap={0.5}
                onClick={handleNavigate}
              >
                <LeftIcon
                  height={20}
                  width={20}
                  fill="#1B1C1E"
                  style={{ cursor: "pointer" }}
                  onClick={handleNavigate}
                />
                My Website
              </Typography>
              <GKButton
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() => setWebsiteConfiguration(true)}
              >
                Publish
              </GKButton>
            </Grid2>
            <Grid2
              container
              width="420px"
              minWidth="420px"
              maxWidth="420px"
              borderRight={"1px solid var(--neutral-100)"}
              flexShrink={0}
              alignContent={"flex-start"}
              height={"100vh"}
            >
              <SectionHeader
                selectedSection={selectedSection}
                onBackClick={handleBackToSections}
                sectionButtons={websiteSectionButtons}
              />

              <Show.When isTrue={selectedSection === null}>
                <Grid2
                  size={12}
                  container
                  spacing={2}
                  overflow={"auto"}
                  height={"calc(100vh - 110px)"}
                  pb={2}
                  alignContent={"flex-start"}
                >
                  <Grid2 size={12} container spacing={1} px={2}>
                    {map(websiteSectionButtons, (item) => (
                      <WebsiteSectionCard
                        key={item.value}
                        item={item}
                        basketList={data?.data?.websiteSection?.myBaskets}
                        pmShowOnWebsite={
                          data?.data?.websiteSection?.performanceMetrices
                            ?.pmShowOnWebsite
                        }
                        onClick={() => handleSectionClick(item.value)}
                      />
                    ))}
                  </Grid2>
                  <Grid2 size={12} px={2}>
                    <Typography
                      fontSize={14}
                      fontWeight={500}
                      color="var(--neutral-700)"
                      sx={{ cursor: "pointer" }}
                      bgcolor={
                        openExtraSection === "WEBSITE_CONFIGURATION"
                          ? "var(--primary-50)"
                          : undefined
                      }
                      onClick={() =>
                        handleOpenExtraSection("WEBSITE_CONFIGURATION")
                      }
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      p={"12px 16px"}
                      borderRadius={"6px"}
                    >
                      Website Configuration
                      <RightIcon />
                    </Typography>
                  </Grid2>
                  <Grid2 container size={12} px={2} spacing={1}>
                    <Grid2 size={12}>
                      <Typography
                        fontSize={14}
                        fontWeight={700}
                        color="var(--neutral-900)"
                        display={"flex"}
                        alignItems="center"
                        gap={1}
                      >
                        Compliance{" "}
                        <span
                          style={{
                            fontWeight: 500,
                            color: "var(--neutral-700)",
                          }}
                        >
                          (footer links)
                        </span>
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      {map(websiteExtraSection, (dataItem) => (
                        <ExtraSectionItem
                          key={dataItem.value}
                          dataItem={dataItem}
                          isOpen={openExtraSection === dataItem.value}
                          onClick={() => handleOpenExtraSection(dataItem.value)}
                        />
                      ))}
                    </Grid2>
                  </Grid2>
                </Grid2>
              </Show.When>

              <Show.When isTrue={selectedSection !== null}>
                <Grid2
                  size={12}
                  p={2}
                  overflow={"auto"}
                  height={"calc(100vh - 184px)"}
                >
                  <WebsiteForm />
                </Grid2>
                <Grid2 size={12}>
                  <FormFooter
                    selectedSection={selectedSection}
                    form={form}
                    loading={
                      isLoading ||
                      isLoadingComplaints ||
                      isFetching ||
                      isLoadingFaqEntries ||
                      isLoadingTestimonials
                    }
                  />
                </Grid2>
              </Show.When>
            </Grid2>

            <Grid2 sx={{ flex: 1, minWidth: 0 }}>
              <Show.When isTrue={!!openExtraSection}>
                <Grid2 container size={12}>
                  <Grid2
                    size={12}
                    maxHeight={"calc(100vh - 129px)"}
                    minHeight={"calc(100vh - 129px)"}
                    overflow={"auto"}
                  >
                    <WebsiteForm />
                  </Grid2>
                  <Grid2
                    size={12}
                    display={"flex"}
                    justifyContent={"flex-end"}
                    p={2}
                    borderTop={"1px solid var(--neutral-100)"}
                  >
                    <GKButton
                      variant="contained"
                      form="my-website-form"
                      startIcon={<CheckIcon />}
                      type="submit"
                      disabled={!form.formState.isDirty || isFetching}
                    >
                      Save Changes
                    </GKButton>
                  </Grid2>
                </Grid2>
              </Show.When>

              <Show.When isTrue={!openExtraSection}>
                <Grid2
                  size={12}
                  component={"span"}
                  display={"flex"}
                  alignItems={"center"}
                  justifyContent={"center"}
                  gap={1}
                  p={2}
                  borderBottom={"1px solid var(--neutral-100)"}
                >
                  <IconButton
                    sx={{
                      ...(previewMode === "desktop" && {
                        borderRadius: 2,
                        bgcolor: "var(--primary-100)",
                      }),
                    }}
                    onClick={() => modeChangeHandler("desktop")}
                  >
                    <DesktopIcon />
                  </IconButton>
                  <IconButton
                    sx={{
                      ...(previewMode === "mobile" && {
                        borderRadius: 2,
                        bgcolor: "var(--primary-100)",
                      }),
                    }}
                    onClick={() => modeChangeHandler("mobile")}
                  >
                    <MobileIcon />
                  </IconButton>
                  <IconButton
                    sx={{
                      ...(previewMode === "tablet" && {
                        borderRadius: 2,
                        bgcolor: "var(--primary-100)",
                      }),
                    }}
                    onClick={() => modeChangeHandler("tablet")}
                  >
                    <TabletIcon />
                  </IconButton>
                </Grid2>
                <Grid2
                  size={12}
                  p={2}
                  display={"flex"}
                  justifyContent={"center"}
                  height={"calc(100vh - 140px)"}
                >
                  <Preview />
                </Grid2>
              </Show.When>
            </Grid2>
          </Grid2>
        </form>
      </FormProvider>

      {websiteConfiguration && (
        <InvestorSettingsConfirmation
          open={websiteConfiguration}
          setOpen={setWebsiteConfiguration}
          investorData={data?.data}
        />
      )}
    </>
  );
};

export default MyWebsite;
